/**
 * DecisionFlow — RepShell
 * ui/src/shells/RepShell.jsx
 * S73-UI-04
 *
 * Shell for user_type = "rep". Route root: /rep/
 *
 * Three panels (spec v1.3 §3.4):
 *   /rep/payout    — MyPayout
 *   /rep/statement — MyStatement
 *   /rep/disputes  — Disputes
 *
 * Architecture rules:
 *   DD-04: No cross-shell imports. Shared/ only.
 *   DD-05: Role always from JWT user_type. Never from UI state.
 *   DD-06: Every value from live API. No hardcoded display values in prod.
 *   §1.3: Rep sees own data only. Server enforces position scoping.
 *
 * API endpoints (api_contract v1.10):
 *   GET /api/v1/payout-summaries?is_latest_final=true
 *     Rep's own payout summary — server enforces position scope.
 *     Fields: total_payout, attainment_pct, arr_payout, mbo_payout,
 *             nps_payout, adoption_payout, ote_reporting, payout_as_pct_ote
 *   GET /api/v1/statement-batches
 *     Rep sees released batches only — server-enforced (§12-S RBAC).
 *   GET /api/v1/fiscal-periods?period_status=open
 *     Active period for ContextHeader — DD-06, spec §6.5.
 *
 * Disputes — api_contract v1.10 §18.1:
 *   POST /api/v1/disputes RBAC is comp_admin only.
 *   Rep cannot submit disputes directly in v1.
 *   "Raise Dispute" button present per spec §6.5 — surfaces contact-admin
 *   modal only. No API call. Future: dedicated rep dispute endpoint.
 *
 * Sensitive read logging (api_contract §1.10):
 *   rep accessing own payout statement → audit_log event: read.payout_statement
 *   This is server-side — no client action required.
 *
 * Copilot: optional, Standard+ tier only (spec §7, api_contract §20.2).
 *   tier comes from JWT claim — checked before rendering CopilotPanel.
 *   Rep copilot scope: explain own payout only.
 */

import { useState, useEffect } from "react";
import { Routes, Route, NavLink, Navigate, useNavigate, useLocation, Link } from "react-router-dom";
import { WalletCards, FileText, MessageSquareText, X, Download, AlertTriangle } from "lucide-react";
import { useAuth } from "../auth/AuthContext.jsx";
import Button from "../components/shared/Button.jsx";
import Card from "../components/shared/Card.jsx";
import CopilotPanel from "../components/shared/CopilotPanel.jsx";
import Field from "../components/shared/Field.jsx";
import Pill from "../components/shared/Pill.jsx";
import TopBar from "../components/shared/TopBar.jsx";
import ThemeButton from "../components/shared/ThemeButton.jsx";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const API_BASE = "/api/v1";

// ---------------------------------------------------------------------------
// Data hooks — each panel owns its fetch (DD-06)
// Cookie auth only — no Bearer token (S160 auth fix)
// ---------------------------------------------------------------------------

function useFiscalPeriods() {
  const [periods, setPeriods] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch(`${API_BASE}/fiscal-periods`, { credentials: "include" })
      .then((r) => r.json())
      .then((data) => {
        const all = data?.data ?? [];
        const sorted = all
          .slice()
          .sort((a, b) => new Date(b.period_start_date) - new Date(a.period_start_date));
        setPeriods(sorted);
      })
      .catch(() => setError("Periods unavailable"));
  }, []);

  return { periods, error };
}

function usePayoutSummary(fiscalPeriodId) {
  const [payout, setPayout] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!fiscalPeriodId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setPayout(null);

    // Step 1 — list endpoint to find payout_summary_id for this period
    fetch(
      `${API_BASE}/payout-summaries?fiscal_period_id=${fiscalPeriodId}&is_latest_final=true`,
      { credentials: "include" }
    )
      .then((r) => { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then((data) => {
        const row = data?.data?.[0] ?? null;
        if (!row) { if (!cancelled) { setPayout(null); setLoading(false); } return; }
        // Step 2 — detail endpoint for full shape including ote_reporting, payout_as_pct_ote
        return fetch(
          `${API_BASE}/payout-summaries/${row.payout_summary_id}`,
          { credentials: "include" }
        )
          .then((r) => { if (!r.ok) throw new Error(r.status); return r.json(); })
          .then((detail) => {
            if (cancelled || !detail) return;
            // Normalise detail response shape to match what MyPayout renders.
            // Detail endpoint uses different field names from list endpoint.
            const arrComp = detail.components?.find(c => c.component_type === "ARR");
            const mboComp = detail.components?.find(c => c.component_type === "MBO");
            const trace   = detail.calculation_trace ?? {};
            const normalised = {
              ...detail,
              attainment_pct:   arrComp?.attainment_pct   ?? null,
              ote_reporting:    trace.ote_midpoint         ?? null,
              arr_payout:       arrComp?.resolved_payout   ?? null,
              mbo_payout:       mboComp?.resolved_payout   ?? "0.00",
              payout_as_pct_ote: (
                trace.ote_midpoint && detail.total_payout
                  ? (parseFloat(detail.total_payout) / parseFloat(trace.ote_midpoint)).toFixed(6)
                  : null
              ),
              // Map resolved_payout → component_payout for component breakdown render
              components: detail.components?.map(c => ({
                ...c,
                component_payout: c.resolved_payout,
                quota:  c.quota,
                credit: c.credit,
              })) ?? [],
            };
            setPayout(normalised);
          });
      })
      .catch((e) => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [fiscalPeriodId]);

  // payout object includes total_payout, attainment_pct, ote_reporting,
  // payout_as_pct_ote, components[], calculation_trace
  return { payout, loading, error };
}

function useStatementBatches() {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch(`${API_BASE}/statement-batches`, { credentials: "include" })
      .then((r) => {
        if (!r.ok) throw new Error(r.status);
        return r.json();
      })
      .then((data) => setBatches(data?.data ?? []))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  return { batches, loading, error };
}

// ---------------------------------------------------------------------------
// LoadingRow — inline skeleton for loading states
// ---------------------------------------------------------------------------

function LoadingRow() {
  return (
    <div className="animate-pulse space-y-2">
      <div className="h-4 w-2/3 rounded skeleton" />
      <div className="h-4 w-1/2 rounded skeleton" />
    </div>
  );
}

function ErrorNote({ message }) {
  return (
    <div className="rounded-xl bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">
      {message ?? "Unable to load data. Please refresh."}
    </div>
  );
}

// ---------------------------------------------------------------------------
// MyPayout panel — with calculation trace + component breakdown (OI-REP-PAYOUT-TRANSPARENCY)
// ---------------------------------------------------------------------------

// AcknowledgmentGate — S616 OI-ACKNOWLEDGMENT-PAYOUT-VISIBILITY-GATE
// Fetches GET /api/v1/my/plan-acknowledgment?fiscal_period_id={id}.
// Returns { acknowledged: bool, loading: bool }.
// Used by MyPayout and MyStatement to mask payout figures until acknowledged.
function useMyAcknowledgment(fiscalPeriodId, refreshKey = 0) {
  const [acknowledged, setAcknowledged] = useState(null); // null = loading
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!fiscalPeriodId) { setLoading(false); return; }
    setLoading(true);
    fetch(
      `${API_BASE}/my/plan-acknowledgment?fiscal_period_id=${fiscalPeriodId}`,
      { credentials: "include" }
    )
      .then((r) => { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then((data) => setAcknowledged(data?.acknowledged === true))
      .catch(() => setAcknowledged(false))
      .finally(() => setLoading(false));
  }, [fiscalPeriodId, refreshKey]);

  return { acknowledged, loading };
}

// useMyStatementAcknowledgment — S657 OI-STATEMENT-ACKNOWLEDGEMENT
// Hits GET /api/v1/my/statement-acknowledgment?fiscal_period_id={id}
// Returns { acknowledged: bool, loading: bool }
function useMyStatementAcknowledgment(fiscalPeriodId, refreshKey = 0) {
  const [acknowledged, setAcknowledged] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!fiscalPeriodId) { setLoading(false); return; }
    setLoading(true);
    fetch(
      `${API_BASE}/my/statement-acknowledgment?fiscal_period_id=${fiscalPeriodId}`,
      { credentials: "include" }
    )
      .then((r) => { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then((data) => setAcknowledged(data?.acknowledged === true))
      .catch(() => setAcknowledged(false))
      .finally(() => setLoading(false));
  }, [fiscalPeriodId, refreshKey]);

  return { acknowledged, loading };
}

// AcknowledgmentFlow — S616 OI-ACKNOWLEDGMENT-PAYOUT-VISIBILITY-GATE
// Fetches plan terms, shows key plan details, then allows one-click acknowledge.
// On success: parent increments refreshKey → acknowledgment re-fetches → payout reveals.
function AcknowledgmentFlow({ fiscalPeriodId, onAcknowledged }) {
  const [termsData, setTermsData] = useState(null);
  const [termsLoading, setTermsLoading] = useState(true);
  const [termsError, setTermsError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [ackError, setAckError] = useState(null);

  useEffect(() => {
    if (!fiscalPeriodId) return;
    fetch(`${API_BASE}/my/plan-terms?fiscal_period_id=${fiscalPeriodId}`, { credentials: "include" })
      .then((r) => { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then(setTermsData)
      .catch((e) => setTermsError(e.message))
      .finally(() => setTermsLoading(false));
  }, [fiscalPeriodId]);

  const terms = termsData?.plan_terms ?? null;
  const pc    = terms?.plan_component ?? null;

  async function handleAcknowledge() {
    if (!terms?.plan_assignment_id) return;
    setSubmitting(true);
    setAckError(null);
    try {
      const res = await fetch(`${API_BASE}/my/plan-acknowledgment`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          plan_assignment_id:    terms.plan_assignment_id,
          fiscal_period_id:      fiscalPeriodId,
          acknowledgment_method: "in_app",
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        const code = body?.detail?.error?.code ?? "ACK_ERROR";
        throw new Error(`[${code}] ${body?.detail?.error?.message ?? `HTTP ${res.status}`}`);
      }
      onAcknowledged();
    } catch (e) {
      setAckError(e.message);
    } finally {
      setSubmitting(false);
    }
  }

  if (termsLoading) return (
    <div className="animate-pulse space-y-2">
      <div className="h-4 w-2/3 rounded skeleton" />
      <div className="h-4 w-1/2 rounded skeleton" />
    </div>
  );

  if (termsError || !terms) return (
    <div className="flex items-start gap-3 rounded-xl border border-[rgb(var(--color-warning))]/40 bg-[rgb(var(--color-warning))]/8 px-4 py-3 text-tok-sm text-[rgb(var(--color-warning))]">
      <span className="mt-0.5 shrink-0">⚠</span>
      <span>No active plan assignment found for this period. Contact your comp admin.</span>
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Plan summary — show what the rep is signing up for */}
      <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-4 space-y-3">
        <p className="text-tok-xs font-semibold uppercase tracking-wider text-[rgb(var(--color-text-muted))]">
          Your Compensation Plan
        </p>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3 text-tok-sm">
          <div>
            <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Plan</p>
            <p className="font-medium text-[rgb(var(--color-text))]">{terms.plan_code ?? "—"}</p>
          </div>
          {pc?.ote_midpoint && (
            <div>
              <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">OTE</p>
              <p className="font-medium text-[rgb(var(--color-text))]">
                {Number(pc.ote_midpoint).toLocaleString("en-US", { style: "currency", currency: pc.ote_currency_code ?? "USD" })}
              </p>
            </div>
          )}
          {pc?.variable_pct && (
            <div>
              <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Variable</p>
              <p className="font-medium text-[rgb(var(--color-text))]">{`${(Number(pc.variable_pct) * 100).toFixed(0)}%`}</p>
            </div>
          )}
          {pc?.arr_weight && Number(pc.arr_weight) > 0 && (
            <div>
              <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">ARR Weight</p>
              <p className="font-medium text-[rgb(var(--color-text))]">{`${(Number(pc.arr_weight) * 100).toFixed(0)}%`}</p>
            </div>
          )}
          {pc?.mbo_weight && Number(pc.mbo_weight) > 0 && (
            <div>
              <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">MBO Weight</p>
              <p className="font-medium text-[rgb(var(--color-text))]">{`${(Number(pc.mbo_weight) * 100).toFixed(0)}%`}</p>
            </div>
          )}
          <div>
            <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Effective</p>
            <p className="font-medium text-[rgb(var(--color-text))]">{terms.effective_date ?? "—"}</p>
          </div>
        </div>
      </div>

      {/* Acknowledge CTA */}
      <div className="flex items-start gap-3 rounded-xl border border-[rgb(var(--color-warning))]/40 bg-[rgb(var(--color-warning))]/8 px-4 py-3">
        <span className="mt-0.5 shrink-0 text-[rgb(var(--color-warning))]">⚠</span>
        <div className="flex-1 space-y-2">
          <p className="text-tok-sm font-medium text-[rgb(var(--color-warning))]">
            Acknowledge your plan to view your earnings.
          </p>
          <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
            By acknowledging, you confirm you have reviewed and understood your compensation plan for this period.
          </p>
          {ackError && (
            <p className="text-tok-xs text-[rgb(var(--color-danger))]">{ackError}</p>
          )}
          <button
            onClick={handleAcknowledge}
            disabled={submitting}
            className="rounded-xl bg-[rgb(var(--color-primary))] px-4 py-2 text-tok-sm font-semibold text-white transition hover:opacity-90 disabled:opacity-50"
          >
            {submitting ? "Acknowledging…" : "Acknowledge My Plan"}
          </button>
        </div>
      </div>
    </div>
  );
}

// StatementAcknowledgmentBanner — S657 OI-STATEMENT-ACKNOWLEDGEMENT
// Shown in MyStatement when batch is released and rep has not yet acknowledged.
// Fetches GET /my/statement-summary for payout figures, then POST /my/statement-acknowledgment.
// Mirrors AcknowledgmentFlow pattern (plan ack gate).
function StatementAcknowledgmentBanner({ fiscalPeriodId, statementBatchId, onAcknowledged }) {
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [ackError, setAckError] = useState(null);

  useEffect(() => {
    if (!fiscalPeriodId) return;
    setSummaryLoading(true);
    fetch(
      `${API_BASE}/my/statement-summary?fiscal_period_id=${fiscalPeriodId}`,
      { credentials: "include" }
    )
      .then((r) => { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then(setSummary)
      .catch(() => setSummary(null))
      .finally(() => setSummaryLoading(false));
  }, [fiscalPeriodId]);

  const fmt = (val) =>
    val != null
      ? Number(val).toLocaleString("en-US", { style: "currency", currency: summary?.payout_currency_code ?? "USD" })
      : "—";

  async function handleAcknowledge() {
    if (!statementBatchId) return;
    setSubmitting(true);
    setAckError(null);
    try {
      const res = await fetch(`${API_BASE}/my/statement-acknowledgment`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          fiscal_period_id:      fiscalPeriodId,
          statement_batch_id:    statementBatchId,
          acknowledgment_method: "in_app",
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        const code = body?.detail?.error?.code ?? "ACK_ERROR";
        throw new Error(`[${code}] ${body?.detail?.error?.message ?? `HTTP ${res.status}`}`);
      }
      onAcknowledged();
    } catch (e) {
      setAckError(e.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-4">
      {/* Payout summary — what the rep is acknowledging */}
      {summaryLoading && <LoadingRow />}
      {!summaryLoading && summary && (
        <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-4 space-y-3">
          <p className="text-tok-xs font-semibold uppercase tracking-wider text-[rgb(var(--color-text-muted))]">
            Your Payout Statement
          </p>
          <div className="grid gap-2 sm:grid-cols-2 text-tok-sm">
            <div>
              <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Total Payout</p>
              <p className="font-medium text-[rgb(var(--color-text))]">{fmt(summary.total_payout_amount)}</p>
            </div>
            <div>
              <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Currency</p>
              <p className="font-medium text-[rgb(var(--color-text))]">{summary.payout_currency_code ?? "—"}</p>
            </div>
            {summary.components?.map((c, i) => (
              <div key={i}>
                <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">{c.component_label}</p>
                <p className="font-medium text-[rgb(var(--color-text))]">{fmt(c.component_amount)}</p>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Acknowledge CTA — mirrors AcknowledgmentFlow pattern */}
      <div className="flex items-start gap-3 rounded-xl border border-[rgb(var(--color-warning))]/40 bg-[rgb(var(--color-warning))]/8 px-4 py-3">
        <span className="mt-0.5 shrink-0 text-[rgb(var(--color-warning))]">⚠</span>
        <div className="flex-1 space-y-2">
          <p className="text-tok-sm font-medium text-[rgb(var(--color-warning))]">
            Acknowledge your statement to confirm receipt.
          </p>
          <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
            By acknowledging, you confirm you have reviewed your payout statement for this period.
            Once acknowledged, the dispute window will close.
          </p>
          {ackError && (
            <p className="text-tok-xs text-[rgb(var(--color-danger))]">{ackError}</p>
          )}
          <button
            onClick={handleAcknowledge}
            disabled={submitting || !statementBatchId}
            className="rounded-xl bg-[rgb(var(--color-primary))] px-4 py-2 text-tok-sm font-semibold text-white transition hover:opacity-90 disabled:opacity-50"
          >
            {submitting ? "Acknowledging…" : "Acknowledge My Statement"}
          </button>
        </div>
      </div>
    </div>
  );
}

function MyPayout({ period }) {
  const fpId = period?.fiscal_period_id;
  const { payout, loading, error } = usePayoutSummary(fpId ?? null);
  const [ackRefreshKey, setAckRefreshKey] = useState(0);
  const { acknowledged, loading: ackLoading } = useMyAcknowledgment(fpId ?? null, ackRefreshKey);

  const fmt = (val) =>
    val != null
      ? Number(val).toLocaleString("en-US", { style: "currency", currency: "USD" })
      : "—";

  const fmtPct = (val) =>
    val != null ? `${(Number(val) * 100).toFixed(1)}%` : "—";

  const components = payout?.components ?? [];
  const trace = payout?.calculation_trace ?? null;

  // Derive zero-payout reason from available data
  function zeroReason() {
    if (!payout || Number(payout.total_payout) !== 0) return null;
    const attPct = Number(payout.attainment_pct ?? 0);
    if (trace?.rate_band_applied === false || trace?.rate_band_applied === null) {
      return "No rate band applied — attainment may be below the minimum threshold for this plan.";
    }
    if (attPct < 0.5) {
      return `Attainment of ${fmtPct(payout.attainment_pct)} is below the minimum payout threshold for this plan.`;
    }
    if (components.length > 0 && components.every((c) => Number(c.component_payout ?? 0) === 0)) {
      return "All components calculated to $0.00. Check quota, credit, and rate table configuration with your comp admin.";
    }
    return "Payout calculated as $0.00. Contact your comp admin for details.";
  }

  const reason = zeroReason();

  return (
    <div className="space-y-4">
      {/* Summary card */}
      <Card
        title="My Payout"
        action={<Pill tone={payout ? "Complete" : "Draft"}>{payout ? "Final" : "Pending"}</Pill>}
      >
        {loading && <LoadingRow />}
        {error && <ErrorNote message="Unable to load payout data." />}
        {!loading && !error && !payout && (
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">No finalised payout available for this period.</p>
        )}
        {fpId && !loading && !ackLoading && !acknowledged && (
          <AcknowledgmentFlow fiscalPeriodId={fpId} onAcknowledged={() => setAckRefreshKey((k) => k + 1)} />
        )}
        {payout && acknowledged && (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <Field label="Total Payout" value={fmt(payout.total_payout)} />
            <Field label="Attainment" value={fmtPct(payout.attainment_pct)} />
            <Field label="OTE" value={fmt(payout.ote_reporting)} />
            <Field label="Payout as % OTE" value={fmtPct(payout.payout_as_pct_ote)} />
            <Field label="ARR Payout" value={fmt(payout.arr_payout)} />
            <Field label="MBO Payout" value={fmt(payout.mbo_payout)} />
            {Number(payout.nps_payout) > 0 && (
              <Field label="NPS Payout" value={fmt(payout.nps_payout)} />
            )}
            {Number(payout.adoption_payout) > 0 && (
              <Field label="Adoption Payout" value={fmt(payout.adoption_payout)} />
            )}
          </div>
        )}

        {/* Zero payout explanation — only after acknowledged */}
        {reason && acknowledged && (
          <div className="mt-4 flex items-start gap-2 rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-warning))] px-3 py-2.5 text-tok-sm text-[rgb(var(--color-warning))]">
            <span className="mt-0.5 shrink-0">⚠</span>
            <span>{reason}</span>
          </div>
        )}
      </Card>

      {/* Component breakdown — only after acknowledged */}
      {components.length > 0 && acknowledged && (
        <Card title="Component Breakdown">
          <div className="space-y-2">
            <div className="grid grid-cols-4 gap-2 px-2 text-tok-xs font-medium uppercase tracking-wide text-[rgb(var(--color-text-muted))]">
              <span>Component</span>
              <span className="text-right">Attainment</span>
              <span className="text-right">Quota / Credit</span>
              <span className="text-right">Payout</span>
            </div>
            {components.map((c, i) => (
              <div
                key={c.payout_detail_id ?? i}
                className="grid grid-cols-4 gap-2 rounded-xl border border-[rgb(var(--color-border))] px-3 py-2.5 text-tok-sm"
              >
                <div className="flex items-center gap-1.5">
                  <span className="font-medium text-[rgb(var(--color-text))]">
                    {c.component_type}
                  </span>
                  {c.ramp_factor && Number(c.ramp_factor) < 1 && (
                    <Pill tone="Draft">Ramped {fmtPct(c.ramp_factor)}</Pill>
                  )}
                </div>
                <span className="text-right text-[rgb(var(--color-text-muted))]">
                  {fmtPct(c.attainment_pct)}
                </span>
                <span className="text-right text-[rgb(var(--color-text-muted))]">
                  {fmt(c.quota)} / {fmt(c.credit)}
                </span>
                {/* §15.2 S578: Held badge when gate_met=FALSE (exception blocking gate fired) */}
                {c.gate_met === false && c.source_ref_id?.startsWith("blocked_by_exception:") ? (
                  <div className="flex items-center justify-end gap-1.5">
                    <span className="font-medium text-[rgb(var(--color-text))]">$0.00</span>
                    <Pill tone="Warning">
                      <Link
                        to={`/exceptions/${c.source_ref_id.replace("blocked_by_exception:", "")}`}
                        className="flex items-center gap-1"
                      >
                        <AlertTriangle className="h-3 w-3" />
                        Held
                      </Link>
                    </Pill>
                  </div>
                ) : (
                  <span className="text-right font-medium text-[rgb(var(--color-text))]">
                    {fmt(c.component_payout)}
                  </span>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Calculation trace */}
      {/* Calculation Detail — only after acknowledged */}
      {trace && acknowledged && (
        <Card title="Calculation Detail">

          {/* ── Proof steps ─────────────────────────────────────────────────
              Grouped by section. Each section gets a header and its steps.
              value_type drives display formatting:
                currency → value already pre-formatted by router as $X,XXX.XX
                percent  → already formatted as XX.XX%
                raw      → display as-is
              is_negative → danger colour (clawback / recovery)
              is_zero     → muted warning colour (zero payout diagnostic)
              is_result   → highlighted row (section subtotal)
              is_total    → bold highlighted row (grand total)
          ─────────────────────────────────────────────────────────────────── */}
          {Array.isArray(trace.proof_steps) && trace.proof_steps.length > 0 ? (() => {
            // Group steps by section preserving insertion order
            const sections = [];
            const seen = new Map();
            for (const step of trace.proof_steps) {
              const sec = step.section || "Other";
              if (!seen.has(sec)) {
                seen.set(sec, []);
                sections.push({ section: sec, steps: seen.get(sec) });
              }
              seen.get(sec).push(step);
            }

            return (
              <div className="space-y-4">
                {sections.map(({ section, steps }) => (
                  <div key={section}>
                    {/* Section header — hidden for TOTAL (it renders as a bold step) */}
                    {section !== "TOTAL" && section !== "Status" && (
                      <p className="mb-1 text-tok-xs font-semibold uppercase tracking-widest text-[rgb(var(--color-primary))] opacity-80">
                        {section}
                      </p>
                    )}
                    <div className="space-y-0.5">
                      {steps.map((step, i) => {
                        const isNeg   = step.is_negative;
                        const isZero  = step.is_zero;
                        const isRes   = step.is_result;
                        const isTotal = step.is_total;

                        const rowCls = [
                          "flex items-baseline justify-between gap-3 rounded-lg px-3 py-2 text-tok-sm",
                          isTotal
                            ? "bg-[rgb(var(--color-primary))] bg-opacity-10 font-bold text-[rgb(var(--color-text))]"
                            : isRes
                            ? "bg-[rgb(var(--color-surface-muted))] font-semibold text-[rgb(var(--color-text))]"
                            : "text-[rgb(var(--color-text-muted))]",
                        ].join(" ");

                        const valCls = [
                          "shrink-0 tabular-nums font-mono",
                          isNeg
                            ? "text-[rgb(var(--color-danger))]"
                            : isZero && isRes
                            ? "text-[rgb(var(--color-warning))]"
                            : isTotal || isRes
                            ? "text-[rgb(var(--color-text))]"
                            : "text-[rgb(var(--color-text-muted))]",
                        ].join(" ");

                        return (
                          <div key={i} className={rowCls}>
                            <span className="flex flex-wrap items-baseline gap-x-2 min-w-0">
                              <span>{step.label}</span>
                              {step.note && (
                                <span className="text-tok-xs text-[rgb(var(--color-text-muted))] font-normal opacity-75">
                                  {step.note}
                                </span>
                              )}
                            </span>
                            <span className={valCls}>{step.value}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            );
          })() : (
            /* Fallback summary fields when proof_steps unavailable (old runs) */
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {trace.ote_midpoint != null && (
                <Field label="OTE Midpoint" value={fmt(trace.ote_midpoint)} />
              )}
              {trace.variable_pct != null && (
                <Field
                  label="Variable %"
                  value={`${(Number(trace.variable_pct) * 100).toFixed(1)}%`}
                />
              )}
              {trace.rate_band_applied != null && (() => {
                const band     = trace.rate_band_applied;
                const hasBand  = !!band;
                const hasRange = hasBand && band.from_pct != null && band.to_pct != null;
                const bandLabel = !hasBand
                  ? "No"
                  : hasRange
                  ? `${(Number(band.from_pct) * 100).toFixed(0)}% – ${(Number(band.to_pct) * 100).toFixed(0)}%`
                  : "Yes";
                return <Field label="Rate Band" value={bandLabel} />;
              })()}
              {trace.interpolation != null && (
                <Field label="Interpolation" value={trace.interpolation ? "Yes" : "No"} />
              )}
              {trace.manual_proof && (
                <div className="col-span-full mt-2 rounded-xl bg-[rgb(var(--color-surface-muted))] px-3 py-2.5">
                  <p className="mb-1 text-tok-xs font-medium uppercase tracking-wide text-[rgb(var(--color-text-muted))]">
                    Calculation Proof
                  </p>
                  <p className="font-mono text-tok-xs text-[rgb(var(--color-text-muted))] break-all">
                    {trace.manual_proof}
                  </p>
                </div>
              )}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// MyStatement panel
// ---------------------------------------------------------------------------

// S657 OI-STATEMENT-ACKNOWLEDGEMENT — statement ack gate added.
// When batch released and rep has not acknowledged: show StatementAcknowledgmentBanner.
// After ack: banner dismisses; payout summary visible; dispute window closed server-side.
function MyStatement({ period, batches, batchesLoading, batchesError }) {
  const loading = batchesLoading;
  const error = batchesError;

  // Rep sees released batches only — server-enforced.
  const latest = batches.find(b => b.fiscal_period_id === period?.fiscal_period_id) ?? null;

  // Statement ack gate — S657 OI-STATEMENT-ACKNOWLEDGEMENT
  const [stmtAckRefreshKey, setStmtAckRefreshKey] = useState(0);
  const { acknowledged: stmtAcknowledged, loading: stmtAckLoading } =
    useMyStatementAcknowledgment(period?.fiscal_period_id ?? null, stmtAckRefreshKey);

  // Rep's own payout from payout-summaries — scoped server-side by position_id.
  const { payout } = usePayoutSummary(latest?.fiscal_period_id ?? period?.fiscal_period_id ?? null);

  const fmt = (val) =>
    val != null
      ? Number(val).toLocaleString("en-US", { style: "currency", currency: "USD" })
      : "—";

  return (
    <div className="space-y-4">
      <Card title="My Statement">
        {loading && <LoadingRow />}
        {error && <ErrorNote message="Unable to load statement data." />}
        {!loading && !error && !latest && (
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
            No released statement available for this period.
          </p>
        )}
        {latest && (
          <>
            {/* Statement ack banner — shown until rep acknowledges */}
            {!stmtAckLoading && !stmtAcknowledged && (
              <div className="mb-4">
                <StatementAcknowledgmentBanner
                  fiscalPeriodId={period?.fiscal_period_id ?? null}
                  statementBatchId={latest?.statement_batch_id ?? null}
                  onAcknowledged={() => setStmtAckRefreshKey((k) => k + 1)}
                />
              </div>
            )}
            {/* Statement detail — always visible once batch is released */}
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Period" value={period?.period_label ?? period?.period_code ?? "—"} />
              <Field label="Status" value="Released" />
              <Field label="Total Payout" value={
                stmtAckLoading ? "—"
                : !stmtAcknowledged ? "Acknowledge statement to view"
                : payout ? fmt(payout.total_payout) : "—"
              } />
              <Field label="Currency" value={latest.payout_currency_code ?? "—"} />
            </div>
            <div className="mt-4">
              {/* PDF export — disabled v1, future session per spec §6.5 OI-UI-REP-EXPORT */}
              <Button variant="secondary" disabled>
                <Download className="h-4 w-4" />
                Download PDF
              </Button>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Disputes panel
// ---------------------------------------------------------------------------

function Disputes({ period }) {
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <div className="space-y-4">
      <Card title="Disputes">
        <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
          If you believe your payout or statement contains an error, contact
          your compensation administrator to raise a dispute.
        </p>
        <div className="mt-4">
          <Button onClick={() => setModalOpen(true)}>
            <MessageSquareText className="h-4 w-4" />
            Raise Dispute
          </Button>
        </div>
      </Card>

      {/* Contact-admin modal — api_contract v1.10 §18.1: POST /disputes is comp_admin only */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-2xl bg-[rgb(var(--color-surface))] p-5 shadow-xl">
            <div className="flex items-start justify-between">
              <h3 className="text-tok-base font-bold text-[rgb(var(--color-text))]">
                Raise a Dispute
              </h3>
              <button
                onClick={() => setModalOpen(false)}
                className="rounded-lg p-1 text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))]"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className="mt-3 text-tok-sm text-[rgb(var(--color-text-muted))]">
              Disputes are managed by your compensation administrator. Please
              contact them directly with the details of your concern, including
              the period and the specific payout component in question.
            </p>
            <p className="mt-2 text-tok-sm text-[rgb(var(--color-text-muted))]">
              Self-service dispute submission will be available in a future
              release.
            </p>
            <div className="mt-5 flex justify-end">
              <Button onClick={() => setModalOpen(false)}>Close</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// RepCopilotCard removed S241 — replaced by shared CopilotPanel.

// ---------------------------------------------------------------------------
// Rep ContextHeader — with period selector (OI-PERIOD-SCOPING)
// ---------------------------------------------------------------------------

function RepContextHeader({ pageTitle, periods, selectedPeriod, onPeriodChange }) {
  return (
    <div className="mb-4 flex flex-wrap items-start justify-between gap-2">
      <h1 className="text-tok-lg font-bold text-[rgb(var(--color-text))]">
        {pageTitle}
      </h1>
      {periods && periods.length > 0 && (
        <select
          value={selectedPeriod?.fiscal_period_id ?? ""}
          onChange={(e) => {
            const p = periods.find((x) => String(x.fiscal_period_id) === e.target.value);
            if (p) onPeriodChange(p);
          }}
          className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-1.5 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none"
        >
          {periods.map((p) => (
            <option key={p.fiscal_period_id} value={p.fiscal_period_id}>
              {p.period_label ?? p.period_code}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Rep Left Nav
// ---------------------------------------------------------------------------

const NAV_ITEMS = [
  { to: "/rep/payout",    label: "My Payout",   icon: WalletCards },
  { to: "/rep/statement", label: "My Statement", icon: FileText },
  { to: "/rep/disputes",  label: "Disputes",     icon: MessageSquareText },
];

function RepNav({ mobileOpen, onClose }) {
  return (
    <>
      {mobileOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/40 lg:hidden"
          onClick={onClose}
        />
      )}
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-52 overflow-y-auto border-r border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] pt-16 lg:static lg:pt-0 transition-transform ${mobileOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}
      >
        <nav className="p-3 space-y-1">
          {NAV_ITEMS.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              onClick={onClose}
              className={({ isActive }) =>
                `flex w-full items-center gap-2.5 rounded-xl px-2.5 py-2 text-tok-sm font-medium transition ${
                  isActive
                    ? "bg-[rgb(var(--color-primary))] text-white"
                    : "text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))]"
                }`
              }
            >
              <Icon className="h-4 w-4 shrink-0" />
              {label}
            </NavLink>
          ))}
        </nav>
      </aside>
    </>
  );
}

// ---------------------------------------------------------------------------
// Page title resolver
// ---------------------------------------------------------------------------

const PAGE_TITLES = {
  "/rep/payout":    "My Payout",
  "/rep/statement": "My Statement",
  "/rep/disputes":  "Disputes",
};

// ---------------------------------------------------------------------------
// RepShell root
// ---------------------------------------------------------------------------

export default function RepShell() {
  const { user } = useAuth();
  // M040 C0: dark/setDark state and useEffect removed — ThemeButton is self-contained.
  const [mobileOpen, setMobileOpen] = useState(false);
  const [copilotOpen, setCopilotOpen] = useState(true);

  // Period selector — 5 most recent periods (OI-PERIOD-SCOPING)
  const { periods } = useFiscalPeriods();
  const { batches, loading: batchesLoading, error: batchesError } = useStatementBatches();
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  useEffect(() => {
    if (selectedPeriod !== null || periods.length === 0) return;
    // Wait for batches to load before setting default period.
    // Default to the period of the most recent released batch so the rep
    // lands on a period with actual data rather than an empty recent period.
    if (batchesLoading) return;
    const batchPeriodId = batches?.[0]?.fiscal_period_id ?? null;
    const defaultPeriod = batchPeriodId
      ? (periods.find((p) => p.fiscal_period_id === batchPeriodId) ?? periods[0])
      : periods[0];
    setSelectedPeriod(defaultPeriod);
  }, [periods, batches, batchesLoading]);

  // S77 — tenant-context: proactive tier gate (Stage 10). ER-1 compliant.
  const [tenantContext, setTenantContext] = useState(null);
  useEffect(() => {
    fetch(`${API_BASE}/tenant-context`, { credentials: "include" })
      .then((r) => r.ok ? r.json() : null)
      .then(setTenantContext)
      .catch(() => {});
  }, []);
  const aiQueryEnabled = tenantContext?.features?.allows_ai_query ?? null;

  const { pathname } = useLocation();
  const pageTitle = PAGE_TITLES[pathname] ?? "My Payout";

  return (
    <div className="min-h-screen bg-[rgb(var(--color-bg))] text-[rgb(var(--color-text))]">
      <TopBar
        userName={user?.full_name ?? user?.email ?? null}
        userRole={user?.user_type ?? "rep"}
        userRoleLabel="Rep"
        mode={import.meta.env.VITE_APP_MODE === "dev" ? "dev" : "prod"}
        onMenuOpen={() => setMobileOpen(true)}
        copilotOpen={copilotOpen}
        onToggleCopilot={() => setCopilotOpen((v) => !v)}
      />

      <div className="flex min-h-[calc(100vh-64px)]">
        <RepNav mobileOpen={mobileOpen} onClose={() => setMobileOpen(false)} />

        <main className="min-w-0 flex-1 p-4 lg:p-6">
          <RepContextHeader
            pageTitle={pageTitle}
            periods={periods}
            selectedPeriod={selectedPeriod}
            onPeriodChange={setSelectedPeriod}
          />

          <Routes>
            <Route index element={<Navigate to="payout" replace />} />
            <Route path="payout"    element={<MyPayout period={selectedPeriod} />} />
            <Route path="statement" element={<MyStatement period={selectedPeriod} batches={batches} batchesLoading={batchesLoading} batchesError={batchesError} periods={periods} />} />
            <Route path="disputes"  element={<Disputes period={selectedPeriod} />} />
            <Route path="*"         element={<Navigate to="payout" replace />} />
          </Routes>

        </main>

        {/* Copilot — shared CopilotPanel, S241 wiring. Sibling to main, not child. */}
        <CopilotPanel
          open={copilotOpen}
          fiscalPeriodId={selectedPeriod?.fiscal_period_id ?? null}
          stageId={null}
          stageName={null}
          aiQueryEnabled={aiQueryEnabled}
        />
      </div>
    </div>
  );
}
